import React, { useCallback, useMemo, useRef, useState } from "react";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { createWbrPublishUploadUrls } from "../graphql/mutations";
import {
  CreateWbrPublishUploadUrlsInput,
  CreateWbrPublishUploadUrlsMutation,
} from "../models/API";

const NAVY = "#232F3E";
const ACCENT_ORANGE = "#FF9900";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB — WBR decks exceed the 10 MB chat cap
const MAX_FILES = 25; // mirrors the Lambda MAX_FILES_PER_BATCH
const UPLOAD_CONCURRENCY = 4;

// Callout-generation polling: the wbr_callout_generator Lambda runs async after
// the S3 object lands. Poll until each uploaded PDF has callouts, or give up.
const GEN_POLL_INTERVAL_MS = 12_000;
const GEN_POLL_TIMEOUT_MS = 8 * 60_000; // 8 minutes

type FileStage = "uploading" | "uploaded" | "generating" | "ready" | "error";

interface FileProgress {
  name: string; // sanitized, lower-cased base name (matches report filename)
  displayName: string; // original name for display
  stage: FileStage;
  /** 0..1 upload fraction. */
  uploadPct: number;
  error?: string;
}

interface WbrPublishUploadButtonProps {
  /** Team that owns the report being viewed (FINN team is 1). Reverse-mapped to
   *  the canonical folder prefix server-side. */
  teamId: number;
  /** WBR week-END date (Saturday). Formatted YYYY-MM-DD to match the pipeline. */
  weekEndDate: Date;
  /** True when the current user is an admin (team / group / FINN). Parent gates
   *  rendering on this; the Lambda re-verifies server-side. */
  canUpload: boolean;
  /** Uploads are only accepted for the current WBR week (mirrors VESL). When
   *  false the button is disabled with an explanatory tooltip. */
  isCurrentWeek?: boolean;
  /** Called after a batch of PUTs settles, with the S3 keys that succeeded. */
  onUploaded?: (uploadedKeys: string[]) => void;
  /** Given the sanitized source PDF file names to check, returns the subset
   *  (lower-cased) whose callouts have been generated. Drives the
   *  generation-progress phase. Authoritative — probes the callout bucket. */
  fetchReadyFilenames?: (fileNames: string[]) => Promise<Set<string>>;
  disabled?: boolean;
}

/** Format a Date as YYYY-MM-DD in local time (the week-end Saturday). */
function formatWeek(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** PUT a file to a presigned URL with real upload-progress events. */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (fraction: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", "application/pdf");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () =>
      reject(new Error("Network or CORS error — the upload was blocked"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.send(file);
  });
}

const stageMeta: Record<
  FileStage,
  { label: string; color: string; icon: React.ReactNode }
> = {
  uploading: { label: "Uploading", color: ACCENT_ORANGE, icon: null },
  uploaded: { label: "Uploaded", color: "#0972D3", icon: null },
  generating: {
    label: "Generating callouts",
    color: "#0972D3",
    icon: <AutorenewIcon fontSize="inherit" className="wbr-spin" />,
  },
  ready: {
    label: "Ready",
    color: "#037F51",
    icon: <CheckCircleIcon fontSize="inherit" />,
  },
  error: {
    label: "Failed",
    color: "#D91515",
    icon: <ErrorOutlineIcon fontSize="inherit" />,
  },
};

const WbrPublishUploadButton: React.FC<WbrPublishUploadButtonProps> = ({
  teamId,
  weekEndDate,
  canUpload,
  isCurrentWeek = true,
  onUploaded,
  fetchReadyFilenames,
  disabled = false,
}) => {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<FileProgress[]>([]);
  const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const client = useMemo(() => generateAuditedClient(), []);

  const week = formatWeek(weekEndDate);

  const patchFile = useCallback(
    (name: string, patch: Partial<FileProgress>) => {
      setFiles((prev) =>
        prev.map((f) => (f.name === name ? { ...f, ...patch } : f))
      );
    },
    []
  );

  /** Poll the report list until every uploaded file has callouts (or timeout). */
  const pollGeneration = useCallback(
    async (pending: FileProgress[]) => {
      if (!fetchReadyFilenames || pending.length === 0) return;
      const deadline = Date.now() + GEN_POLL_TIMEOUT_MS;
      const remaining = new Set(pending.map((f) => f.name));

      while (remaining.size > 0 && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, GEN_POLL_INTERVAL_MS));
        let ready: Set<string>;
        try {
          ready = await fetchReadyFilenames(Array.from(remaining));
        } catch {
          continue; // transient — keep polling until the deadline
        }
        for (const name of Array.from(remaining)) {
          if (ready.has(name)) {
            patchFile(name, { stage: "ready" });
            remaining.delete(name);
          }
        }
      }
      // Anything still pending after the timeout: leave in "generating" — the
      // pipeline may simply be slow; the user can refresh later.
    },
    [fetchReadyFilenames, patchFile]
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-select
    if (selected.length === 0) return;

    setBanner(null);

    if (selected.length > MAX_FILES) {
      setBanner(`Select at most ${MAX_FILES} files at once.`);
      setPanelAnchor(buttonRef.current);
      return;
    }
    for (const f of selected) {
      if (f.type !== "application/pdf") {
        setBanner(`Only PDF files are allowed: ${f.name}`);
        setPanelAnchor(buttonRef.current);
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        setBanner(`"${f.name}" exceeds the 50 MB limit.`);
        setPanelAnchor(buttonRef.current);
        return;
      }
    }

    // Seed progress rows and open the panel.
    const initial: FileProgress[] = selected.map((f) => ({
      name: f.name.toLowerCase(),
      displayName: f.name,
      stage: "uploading",
      uploadPct: 0,
    }));
    setFiles(initial);
    setPanelAnchor(buttonRef.current);
    setBusy(true);

    try {
      const input: CreateWbrPublishUploadUrlsInput = {
        team_id: teamId,
        week,
        file_names: selected.map((f) => f.name),
        content_type: "application/pdf",
      };
      const result = (await client.graphql({
        query: createWbrPublishUploadUrls,
        variables: { input },
      })) as { data: CreateWbrPublishUploadUrlsMutation };

      const payload = result.data.createWbrPublishUploadUrls;
      if (!payload) throw new Error("No upload targets returned.");
      const targets = payload.targets;

      // The server sanitizes file names (basename, lower-case, spaces->_, strip
      // unsafe chars), so the stored S3 object — and its _callouts.html — use the
      // SANITIZED name, not the original. Re-key each tracking row to the
      // sanitized name (targets preserve input order) so progress patches and
      // the callout-status poll match what actually landed in S3.
      setFiles((prev) =>
        prev.map((f, i) =>
          targets[i] ? { ...f, name: targets[i].file_name.toLowerCase() } : f
        )
      );

      // Bounded-concurrency PUTs with per-file progress (keyed by sanitized name).
      const okNames: string[] = [];
      const okKeys: string[] = [];
      let cursor = 0;
      const worker = async () => {
        while (cursor < targets.length) {
          const idx = cursor++;
          const target = targets[idx];
          const file = selected[idx];
          const key = target.file_name.toLowerCase();
          try {
            await putWithProgress(target.upload_url, file, (frac) =>
              patchFile(key, { uploadPct: frac })
            );
            patchFile(key, { stage: "uploaded", uploadPct: 1 });
            okNames.push(key);
            okKeys.push(target.file_key);
          } catch (err) {
            patchFile(key, {
              stage: "error",
              error: err instanceof Error ? err.message : String(err),
            });
          }
        }
      };
      await Promise.all(
        Array.from(
          { length: Math.min(UPLOAD_CONCURRENCY, targets.length) },
          worker
        )
      );

      onUploaded?.(okKeys);

      // Transition successful uploads into the generation-tracking phase, keyed
      // by the sanitized names (which the status poll will match).
      okNames.forEach((name) => patchFile(name, { stage: "generating" }));
      const pendingRows: FileProgress[] = okNames.map((name) => ({
        name,
        displayName: name,
        stage: "generating",
        uploadPct: 1,
      }));
      // Fire-and-forget: polling updates rows as callouts appear.
      void pollGeneration(pendingRows);
    } catch (err) {
      console.error("WBR publish upload failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      const friendly = /unauthorized/i.test(msg)
        ? "You are not authorized to upload PDFs for this team."
        : "Upload failed. Please try again.";
      setBanner(friendly);
      setFiles((prev) =>
        prev.map((f) =>
          f.stage === "uploading" ? { ...f, stage: "error", error: msg } : f
        )
      );
    } finally {
      setBusy(false);
    }
  };

  if (!canUpload) return null;

  const uploadDisabled = disabled || busy || !isCurrentWeek;
  const tooltip = !isCurrentWeek
    ? "Uploads are only allowed for the current WBR week"
    : `Upload WBR PDF(s) for week ${week}`;

  const anyActive = files.some(
    (f) => f.stage === "uploading" || f.stage === "generating"
  );
  const readyCount = files.filter((f) => f.stage === "ready").length;

  return (
    <>
      <style>{`
        @keyframes wbr-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .wbr-spin { animation: wbr-spin 1s linear infinite; }
      `}</style>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        multiple
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      <Tooltip title={tooltip}>
        <span>
          <Button
            ref={buttonRef}
            size="small"
            variant="contained"
            disableElevation
            startIcon={
              busy ? (
                <CircularProgress size={15} sx={{ color: NAVY }} />
              ) : (
                <UploadFileIcon />
              )
            }
            onClick={() => {
              if (files.length > 0) {
                // Re-open the progress panel if there's history to show.
                setPanelAnchor(buttonRef.current);
              } else {
                fileInputRef.current?.click();
              }
            }}
            disabled={uploadDisabled}
            sx={{
              bgcolor: ACCENT_ORANGE,
              color: NAVY,
              fontWeight: 700,
              fontSize: 13,
              textTransform: "none",
              borderRadius: "20px",
              px: 1.75,
              "&:hover": { bgcolor: "#E88B00" },
              "&.Mui-disabled": {
                bgcolor: "rgba(0,0,0,0.12)",
                color: "rgba(0,0,0,0.4)",
              },
            }}
          >
            Upload PDF
            {anyActive && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: "#0972D3",
                  animation: "wbr-spin 1s linear infinite",
                }}
              />
            )}
          </Button>
        </span>
      </Tooltip>

      {/* When the user has an upload in flight, offer a "new upload" affordance
          inside the panel; the button itself reopens the panel. */}
      <Popover
        open={Boolean(panelAnchor)}
        anchorEl={panelAnchor}
        onClose={() => setPanelAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 380,
            maxWidth: "90vw",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            bgcolor: NAVY,
            color: "#fff",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
              Upload WBR PDF
            </Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.75 }}>
              Week {week}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setPanelAnchor(null)}
            sx={{ color: "#fff" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {banner && (
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "rgba(217,21,21,0.08)",
              color: "#D91515",
              fontSize: 12.5,
            }}
          >
            {banner}
          </Box>
        )}

        {/* Per-file progress list */}
        <Box sx={{ px: 2, py: 1.5, maxHeight: 320, overflowY: "auto" }}>
          {files.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary", py: 1 }}>
              Select one or more PDF files to upload for this team and week.
            </Typography>
          ) : (
            files.map((f) => {
              const meta = stageMeta[f.stage];
              const pct = Math.round(
                (f.stage === "uploading" ? f.uploadPct : 1) * 100
              );
              const showBar =
                f.stage === "uploading" || f.stage === "generating";
              return (
                <Box key={f.name} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <InsertDriveFileIcon
                      sx={{ fontSize: 18, color: "rgba(0,0,0,0.4)" }}
                    />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={f.displayName}
                    >
                      {f.displayName}
                    </Typography>
                    <Chip
                      size="small"
                      icon={
                        meta.icon ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              fontSize: 14,
                              color: meta.color,
                              ml: 0.5,
                            }}
                          >
                            {meta.icon}
                          </Box>
                        ) : undefined
                      }
                      label={
                        f.stage === "uploading"
                          ? `${meta.label} ${pct}%`
                          : meta.label
                      }
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        color: meta.color,
                        bgcolor: `${meta.color}14`,
                        "& .MuiChip-icon": { color: meta.color },
                      }}
                    />
                  </Box>
                  {showBar && (
                    <LinearProgress
                      variant={
                        f.stage === "uploading"
                          ? "determinate"
                          : "indeterminate"
                      }
                      value={pct}
                      sx={{
                        height: 5,
                        borderRadius: 3,
                        bgcolor: "rgba(0,0,0,0.06)",
                        "& .MuiLinearProgress-bar": { bgcolor: meta.color },
                      }}
                    />
                  )}
                  {f.stage === "error" && f.error && (
                    <Typography
                      sx={{ fontSize: 11, color: "#D91515", mt: 0.25 }}
                    >
                      {f.error}
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {files.length > 0
              ? `${readyCount}/${files.length} ready`
              : "PDF · max 50 MB each"}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: "none", borderRadius: "16px", fontSize: 12.5 }}
          >
            {files.length > 0 ? "Upload more" : "Choose files"}
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default WbrPublishUploadButton;
